import { useMemo, useState, useEffect } from 'react';
import { NavLink, generatePath } from 'react-router-dom';
import {
  Table,
  useQueryState,
  IconSelected,
  IconBlock,
  SelectItemProps,
  PermissionEnum,
  Filters,
  SchemaFilterType,
  IconSandWatch,
  IconCloseCircle,
  IconSelectedCircle,
  IconEdit,
} from '@ui/src';
import { useAppSelector } from '@store';
import Card from '../partial/card/card';
import {
  PspAccountCategory,
  PspAccountItem,
} from '../../hoc/bank-and-psp/types';
import {
  accountsColumns,
  accountsColumnsLabels,
  accountsSchema,
} from '../../hoc/bank-and-psp/variables/accounts';
import { BankRoutes } from '../../routes';
import { AccountsTypes } from './types';

import cn from './info.module.css';

const getVaultStatus = (status: string) => {
  let vaultStatus;
  let vaultStatusIcon;

  switch (status) {
    case 'IN_PROGRESS':
      vaultStatus = 'Linking';
      vaultStatusIcon = <IconSandWatch width={14} height={14} />;
      break;
    case 'FAILED':
      vaultStatus = 'Failed';
      vaultStatusIcon = <IconCloseCircle className={cn.failedStatus} />;
      break;
    case 'DONE':
      vaultStatus = 'Linked';
      vaultStatusIcon = <IconSelectedCircle className={cn.doneStatus} />;
      break;
    default:
      break;
  }

  return { vaultStatus, vaultStatusIcon };
};

const formatAccountsList = ({
  list,
  casp,
  pspAccountsCategories,
}: {
  list: PspAccountItem[];
  casp?: SelectItemProps[];
  pspAccountsCategories?: PspAccountCategory[];
}): PspAccountItem[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    result.url = generatePath(BankRoutes.BANK_AND_PSP_ACCOUNT_PAGE, {
      accountId: result.id,
    });

    const currenciesValues = item?.currencies?.map((currency) => currency.name);

    const render: Record<string, React.JSX.Element> = {
      active: (
        <div className={cn.accountStatus}>
          {item.active ? (
            <IconSelected className={cn.accountStatusIconActive} />
          ) : (
            <IconBlock className={cn.accountStatusIconDisabled} />
          )}
        </div>
      ),
      fullNumber: (
        <div className={cn.accountFullNumber}>
          {result.account.fullNumber}
          {result?.useOwnBankDetails && (
            <div className={cn.detailsOverride}>
              <IconEdit width={16} height={16} />
            </div>
          )}
        </div>
      ),
      isDefaultForExternalOperations: (
        <div>
          {result.isDefaultForExternalOperations ? (
            <IconSelected
              width={15}
              height={13}
              className={cn.overdraftEnabled}
            />
          ) : (
            <div className={cn.overdraftDisabled} />
          )}
        </div>
      ),
      caspId: (
        <div>
          {result.caspId ? (
            casp?.find((caspItem) => caspItem.value === result.caspId)?.name
          ) : (
            <div className={`${cn.overdraftDisabled} ${cn.withoutMargin}`} />
          )}
        </div>
      ),
      pspIntegrationAccountStatus: (
        <div>
          {result?.pspIntegrationAccountStatus ? (
            <div className={cn.vaultStatusContent}>
              <div className={cn.vaultStatusIcon}>
                {
                  getVaultStatus(result?.pspIntegrationAccountStatus)
                    .vaultStatusIcon
                }
              </div>

              {getVaultStatus(result?.pspIntegrationAccountStatus).vaultStatus}
            </div>
          ) : (
            '-'
          )}
        </div>
      ),
      currencyId: <div>{currenciesValues?.join(', ')}</div>,
      categoryId: (
        <div>
          {
            pspAccountsCategories?.find(
              (category) => category.id === result.categoryId,
            )?.name
          }
        </div>
      ),
      overdraftEnabled: (
        <div>
          {result.account?.overdraftEnabled ? (
            <IconSelected
              width={15}
              height={13}
              className={cn.overdraftEnabled}
            />
          ) : (
            <div className={cn.overdraftDisabled} />
          )}
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const Accounts = ({
  bankId,
  accounts,
  isLoading,
  balances,
  integrated,
  accountsError,
  overdraftEnabled,
  setPage,
  handleSubmit,
  filters,
  isFetching,
  takeCount,
  filtersComponent,
  accountsPagination,
  casp,
  pspIntegration,
  onFilter,
  search,
}: AccountsTypes) => {
  const [formattedColumns, setFormattedColumns] = useState<
    { name: string; label: string; hidden: boolean; sortable?: boolean }[]
  >(
    accountsColumns as {
      name: string;
      label: string;
      hidden: boolean;
      sortable?: boolean;
    }[],
  );

  const { sort } = useQueryState(accountsSchema);

  const { activePermissions } = useAppSelector(
    (state) => state.configurationDictionaries,
  );
  const { pspAccountsCategories, generalInfo } = useAppSelector(
    (state) => state.bankAndPsp.info,
  );

  const formattedData = useMemo(() => {
    const data = formatAccountsList({
      list: accounts as PspAccountItem[],
      casp,
      pspAccountsCategories,
    });

    return data;
  }, [accounts, sort, balances, casp, pspAccountsCategories]);

  useEffect(() => {
    if (
      overdraftEnabled ||
      (pspIntegration &&
        (pspIntegration?.name === 'PRIME_VAULT_WALLET' ||
          pspIntegration?.name === 'OPEN_PAYD'))
    ) {
      let nextColumns = (
        [] as {
          name: string;
          label: string;
          hidden: boolean;
          sortable?: boolean;
        }[]
      ).concat(
        accountsColumns as {
          name: string;
          label: string;
          hidden: boolean;
          sortable?: boolean;
        }[],
      );

      if (
        overdraftEnabled &&
        !nextColumns.find((item) => item.name === 'overdraftEnabled')
      ) {
        nextColumns.push({
          name: 'overdraftEnabled',
          label: 'Overdraft',
          hidden: false,
        });
      }

      if (
        pspIntegration &&
        (pspIntegration?.name === 'PRIME_VAULT_WALLET' ||
          pspIntegration?.name === 'OPEN_PAYD' ||
          pspIntegration?.name === 'Keel')
      ) {
        nextColumns = nextColumns?.map((item) => {
          const result = { ...item };

          if (result.name === 'pspIntegrationAccountStatus') {
            result.hidden = false;

            if (
              pspIntegration?.name === 'OPEN_PAYD' ||
              pspIntegration?.name === 'Keel'
            ) {
              result.label = 'External Account Status';
            }
          }

          return result;
        });
      }

      setFormattedColumns(nextColumns);
    }
  }, [overdraftEnabled, pspIntegration]);

  return (
    <Card
      className={`${cn.documentsCard} ${cn.primaryAccountsCardContent}`}
      wrapperClassName={cn.primaryAccountsCard}
      isLoading={isLoading}
      title={
        <div className={cn.infoTitleWrapper}>
          <div
            className={cn.infoTitle}
          >{`${generalInfo?.companyName} - Accounts`}</div>
          {activePermissions?.includes(PermissionEnum.PSP_ACCOUNT_ADD_ACTION) &&
            integrated && (
              <NavLink
                to={`${generatePath(
                  BankRoutes.BANK_AND_PSP_PAGE_CREATE_ACCOUNT,
                  {
                    bankId: bankId as string,
                  },
                )}${search || ''}`}
                className={cn.infoTitleLink}
              >
                <span className={cn.infoTitleLinkIcon}>+</span>
                <span className={cn.infoTitleLinkText}>Add</span>
              </NavLink>
            )}
        </div>
      }
    >
      <Table
        columns={formattedColumns}
        error={accountsError}
        data={formattedData || []}
        onSort={() => {}}
        filters={filters}
        isLoading={isFetching}
        filtersComponent={filtersComponent}
        sort={sort}
        columnsLabels={accountsColumnsLabels}
        onFilterChange={(filterValues) => {
          const nextFilters: Filters = {
            ...filterValues,
            take: {
              value: [takeCount.toString()],
              type: SchemaFilterType.STRING,
            },
          };

          onFilter(nextFilters);
        }}
        tableTopClassName={cn.accountsTableTop}
        handleSubmit={handleSubmit}
        take={takeCount?.toString()}
        setPage={(data) => {
          const filterValues: Filters = {
            ...filters,
            take: {
              value: [takeCount?.toString()],
              type: SchemaFilterType.STRING,
            },
          };

          setPage({ ...data, filters: filterValues });
        }}
        pagination={accountsPagination}
        withScroll
        clickable
      />
    </Card>
  );
};

export default Accounts;
